import { useContext } from "react";
import { isCancelledError } from "@tanstack/react-query";
import {
  Content,
  Text,
  Heading,
  ActionButton,
  ButtonGroup,
  Button,
  ProgressCircle,
  IllustratedMessage,
  LinkButton,
} from "@react-spectrum/s2";
import BugIcon from "@react-spectrum/s2/icons/Bug";
import ExpressIcon from "icon:../../../assets/AdobeExpressIcon.svg";

import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { UserFeedback } from "@app/components/UserFeedback";
import { ModularVideos } from "../ModularVideos";
import { Step } from "./Step";
import { DevToolbarItem } from "../DevToolbar";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const GenerateVideoStep = () => {
  const {
    videoId,
    template,
    video,
    generateVideo,
    exportVideo,
    exportBanners,
    updateVideo,
    isVideoRendered,
    downloadVideoUrl,
    renderProgress,
    generateStatusText,
    statusTextIndex,
  } = useContext(VideoWizardContext);

  const generateError = generateVideo.error;
  const hasExpressError =
    video.data?.templateType === "express" &&
    generateVideo.isError &&
    !generateVideo.isPending &&
    !isCancelledError(generateError) &&
    generateError?.cause?.problem !== "CANCEL_ERROR";

  return (
    <>
      <Step
        styles={style({
          gridArea: "content",
          display: "flex",
          flexDirection: "column",
          gap: 64,
        })}
      >
        {!hasExpressError && (
          <Step.Header title="We're crafting your branded video">
            {template.data?.type === "express" ? (
              <Text styles={style({ font: "body-xl", textAlign: "center" })}>
                This may take a few minutes - don&apos;t close this tab.
                <br />
                Once your video is ready, we will send you an email with the
                link.
              </Text>
            ) : (
              <Text styles={style({ font: "body-xl", textAlign: "center" })}>
                This can take up to 35 minutes.
                <br />
                You don&apos;t need to keep this tab open — we&apos;ll email you
                a link as soon as it&apos;s ready.
              </Text>
            )}
          </Step.Header>
        )}

        {hasExpressError ? (
          <Step.Error
            title="Video generation failed"
            onRetry={() =>
              generateVideo.mutate({
                videoId,
                templateAssetsRenditions: exportBanners.data,
              })
            }
          >
            {generateError?.message ||
              "Something went wrong. Please try again."}
          </Step.Error>
        ) : video.data?.templateType === "modular" && !video.data?.videoPath ? (
          <ModularVideos jobs={video.data?.modularJobs ?? []} />
        ) : (
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 48,
              marginTop: -96,
            })}
          >
            <IllustratedMessage styles={style({ maxWidth: 640 })}>
              <div
                className={style({
                  gridArea: "illustration",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  marginX: "auto",
                  marginBottom: 12,
                })}
              >
                {isVideoRendered ? (
                  <video
                    src={downloadVideoUrl ?? undefined}
                    poster={generateVideo?.data?.document?.thumbnailUrl || ""}
                    controls
                    className={style({
                      display: "block",
                      width: 640,
                      aspectRatio: "640 / 360",
                      borderRadius: "default",
                    })}
                  />
                ) : (
                  <div
                    className={style({
                      width: 640,
                      aspectRatio: "640 / 360",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "default",
                      backgroundColor: "gray-1000/10",
                    })}
                  >
                    <ProgressCircle
                      isIndeterminate={!renderProgress}
                      value={renderProgress}
                      size="L"
                      staticColor="auto"
                      aria-label="Generating video…"
                    />
                  </div>
                )}
                <Text styles={style({ font: "ui", color: "neutral-subdued" })}>
                  {isVideoRendered
                    ? generateVideo?.data?.document?.name?.replace?.(
                        /\.express$/,
                        "",
                      )
                    : generateStatusText[statusTextIndex] ||
                      "Generating video…"}
                </Text>
              </div>
              {isVideoRendered ? (
                <Heading>Your video is ready</Heading>
              ) : (
                <Heading>Generating your video</Heading>
              )}
              <Content>
                {isVideoRendered ? (
                  video.data?.templateType === "express" ? (
                    <Text>
                      Download the video file directly or open in Adobe Express
                      to review and edit.
                    </Text>
                  ) : null
                ) : (
                  <Text>
                    We&apos;re crafting your new branded video. This may take a
                    few moments.
                  </Text>
                )}
              </Content>
              <ButtonGroup styles={style({ marginTop: 24 })}>
                {video.data?.templateType === "express" && (
                  <LinkButton
                    isDisabled={
                      !video.data?.expressId || generateVideo.isPending
                    }
                    variant="secondary"
                    fillStyle="outline"
                    href={`https://new.express.adobe.com/id/${video?.data?.expressId}?category=none`}
                    target="_blank"
                  >
                    <ExpressIcon />
                    <Text>Open in Express</Text>
                  </LinkButton>
                )}
                {downloadVideoUrl ? (
                  <LinkButton
                    variant="accent"
                    href={downloadVideoUrl}
                    download={`${video.data.name}.mp4`}
                    target="_blank"
                  >
                    <Text>Download video</Text>
                  </LinkButton>
                ) : (
                  <Button variant="accent" isDisabled>
                    <Text>Download video</Text>
                  </Button>
                )}
              </ButtonGroup>
            </IllustratedMessage>

            {isVideoRendered && video.data?.id && (
              <UserFeedback
                variant="inline"
                title="How did your video turn out?"
                video={video.data}
                currentStep="video-view"
                method="inline"
                styles={style({ width: "full", maxWidth: 400 })}
              />
            )}

            {isVideoRendered && (
              <IllustratedMessage size="XS">
                <Heading>Do you need help with your project?</Heading>
                <ButtonGroup size="S">
                  <LinkButton
                    variant="secondary"
                    href="https://adobe.ly/ExperienceDesign-Request"
                    target="_blank"
                  >
                    <Text>Request design assist</Text>
                  </LinkButton>
                </ButtonGroup>
              </IllustratedMessage>
            )}
          </div>
        )}
      </Step>

      <DevToolbarItem
        isDisabled={!exportVideo.data?.presignURL}
        onPress={() => {
          updateVideo.mutate(
            {
              expressId: null,
              videoPath: null,
            },
            {
              onSuccess: () =>
                generateVideo.mutateAsync({
                  videoId,
                  templateAssetsRenditions: exportBanners.data,
                }),
            },
          );
        }}
      >
        Regenerate
      </DevToolbarItem>
    </>
  );
};
